import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Filter, RefreshCw } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useAuthStore } from '../store/authStore';
import axios from 'axios';
import toast from 'react-hot-toast';

const severityBadge = { critical: 'bg-red-100 text-red-700', high: 'bg-red-100 text-red-700', medium: 'bg-yellow-100 text-yellow-700', low: 'bg-blue-100 text-blue-700' };

function SymbolRecommendations() {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ engine: '', category: '', severity: '', is_active: '' });
  const [filterOptions, setFilterOptions] = useState({ engines: [], categories: [], severities: [] });
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ symbol_name: '', engine: 'rspamd', title: '', description: '', recommendation: '', severity: 'medium', category: '' });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkUpdateOnly, setBulkUpdateOnly] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [permissions, setPermissions] = useState({ canRead: true, canWrite: false, userEmail: '' });
  const { isAuthenticated } = useAuthStore();

  const fetchRecs = useCallback(async () => {
    try { setLoading(true); const p = new URLSearchParams(); Object.entries(filters).forEach(([k,v]) => { if(v) p.append(k,v); }); const r = await axios.get(`/recommendations/admin/all?${p}`); setRecommendations(r.data.recommendations || []); }
    catch { toast.error('Failed to load'); } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { if (isAuthenticated) { axios.get('/recommendations/admin/filters').then(r => setFilterOptions(r.data.filters || {})).catch(() => {}); axios.get('/recommendations/permissions').then(r => setPermissions(r.data.permissions || {})).catch(() => {}); } }, [isAuthenticated]);
  useEffect(() => { if (isAuthenticated) fetchRecs(); }, [isAuthenticated, filters, fetchRecs]);

  const resetForm = () => { setForm({ symbol_name: '', engine: 'rspamd', title: '', description: '', recommendation: '', severity: 'medium', category: '' }); setFormErrors({}); };

  const validate = () => {
    const e = {};
    if (!form.symbol_name.trim()) e.symbol_name = true;
    if (!form.title.trim()) e.title = true;
    if (!form.recommendation.trim()) e.recommendation = true;
    setFormErrors(e); return Object.keys(e).length === 0;
  };

  const submitForm = async (isEdit) => {
    if (!validate()) return;
    try { setSubmitting(true); if (isEdit) { await axios.put(`/recommendations/admin/${selected.id}`, form); toast.success('Updated'); setEditOpen(false); } else { await axios.post('/recommendations/admin/add', form); toast.success('Added'); setAddOpen(false); } fetchRecs(); resetForm(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const deleteRec = async () => {
    try { setSubmitting(true); await axios.delete(`/recommendations/admin/${selected.id}`); toast.success('Deleted'); setDeleteOpen(false); fetchRecs(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const FormFields = ({ disabled }) => (
    <div className="grid gap-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1">Symbol Name *</label>
          <input value={form.symbol_name} onChange={e => setForm(p => ({...p, symbol_name: e.target.value}))} disabled={disabled}
            className={`w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.symbol_name ? 'border-red-500' : 'border-gray-300'}`} />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Engine</label>
          <select value={form.engine} onChange={e => setForm(p => ({...p, engine: e.target.value}))} disabled={disabled}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500">
            {['rspamd','spamassassin','ai','generic'].map(e => <option key={e} value={e}>{e}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Title *</label>
        <input value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))}
          className={`w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.title ? 'border-red-500' : 'border-gray-300'}`} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1">Severity</label>
          <select value={form.severity} onChange={e => setForm(p => ({...p, severity: e.target.value}))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500">
            {['low','medium','high','critical'].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Category</label>
          <input value={form.category} onChange={e => setForm(p => ({...p, category: e.target.value}))} placeholder="e.g., headers, content"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Description</label>
        <textarea value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} rows={2}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 resize-y" />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Recommendation *</label>
        <textarea value={form.recommendation} onChange={e => setForm(p => ({...p, recommendation: e.target.value}))} rows={3}
          className={`w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 resize-y ${formErrors.recommendation ? 'border-red-500' : 'border-gray-300'}`} />
      </div>
    </div>
  );

  const Modal = ({ open, onClose, title, children }) => open ? (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-bold mb-4">{title}</h3>{children}
      </div>
    </div>
  ) : null;

  if (!isAuthenticated) return <div className="text-center py-16 text-gray-500 text-xl">Please log in</div>;

  return (
    <>
      <Helmet><title>Symbol Recommendations - Rspamd Email Scanner</title></Helmet>
      <div>
        <div className="flex items-center justify-between mb-6">
          <div><h2 className="text-2xl font-bold mb-1">Symbol Recommendations</h2><p className="text-gray-500">Manage recommendations for spam filter symbols</p></div>
          <div className="flex items-center gap-2">
            <button onClick={fetchRecs} className="p-2 rounded-lg hover:bg-gray-100 text-blue-600"><RefreshCw className="w-5 h-5" /></button>
            {permissions.canWrite && <button onClick={() => { resetForm(); setAddOpen(true); }} className="flex items-center gap-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium"><Plus className="w-4 h-4" />Add</button>}
            {permissions.canWrite && <button onClick={() => setBulkOpen(true)} className="border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 text-sm font-medium">Bulk Upload</button>}
          </div>
        </div>

        {!permissions.canWrite && (
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded text-sm mb-4">
            <strong>Read-only access:</strong> A local admin or collaborator role is required to edit. Current: <strong>{permissions.userEmail}</strong>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border p-4 mb-4">
          <div className="flex items-center gap-2 mb-2 text-sm font-semibold"><Filter className="w-4 h-4" /> Filters</div>
          <div className="flex flex-wrap gap-3">
            {[{ key: 'engine', opts: filterOptions.engines }, { key: 'category', opts: filterOptions.categories }, { key: 'severity', opts: filterOptions.severities }].map(f => (
              <select key={f.key} value={filters[f.key]} onChange={e => setFilters(p => ({...p, [f.key]: e.target.value}))}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">All {f.key}s</option>
                {(f.opts || []).map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ))}
            <select value={filters.is_active} onChange={e => setFilters(p => ({...p, is_active: e.target.value}))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">All statuses</option><option value="true">Active</option><option value="false">Inactive</option>
            </select>
            <button onClick={() => setFilters({ engine: '', category: '', severity: '', is_active: '' })} className="border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 text-sm">Clear</button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          {loading ? <div className="flex justify-center p-8"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" /></div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b"><tr>
                  {['Symbol','Engine','Title','Category','Severity','Status','Actions'].map(h => <th key={h} className="px-4 py-3 text-left font-semibold">{h}</th>)}
                </tr></thead>
                <tbody className="divide-y">
                  {recommendations.length === 0 ? <tr><td colSpan={7} className="text-center py-8 text-gray-400">No recommendations found</td></tr> :
                    recommendations.map(r => (
                      <tr key={r.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium">{r.symbol_name}</td>
                        <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full text-xs border">{r.engine}</span></td>
                        <td className="px-4 py-3">{r.title.length > 50 ? r.title.substring(0,50)+'...' : r.title}</td>
                        <td className="px-4 py-3">{r.category && <span className="px-2 py-0.5 rounded-full text-xs border">{r.category}</span>}</td>
                        <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${severityBadge[r.severity] || 'bg-gray-100 text-gray-700'}`}>{r.severity}</span></td>
                        <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{r.is_active ? 'Active' : 'Inactive'}</span></td>
                        <td className="px-4 py-3">
                          {permissions.canWrite ? (
                            <div className="flex gap-1">
                              <button onClick={() => { setSelected(r); setForm({ symbol_name: r.symbol_name, engine: r.engine, title: r.title, description: r.description || '', recommendation: r.recommendation, severity: r.severity, category: r.category || '' }); setFormErrors({}); setEditOpen(true); }} className="text-blue-600 hover:text-blue-800 p-1"><Pencil className="w-4 h-4" /></button>
                              <button onClick={() => { setSelected(r); setDeleteOpen(true); }} className="text-red-500 hover:text-red-700 p-1"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          ) : <span className="text-gray-400 text-xs">Read-only</span>}
                        </td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
          )}
        </div>

        <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Add Recommendation">
          <FormFields /><div className="flex justify-end gap-3 mt-4"><button onClick={() => setAddOpen(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={() => submitForm(false)} disabled={submitting} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">{submitting ? 'Saving...' : 'Add'}</button></div>
        </Modal>

        <Modal open={editOpen} onClose={() => setEditOpen(false)} title="Edit Recommendation">
          <FormFields disabled /><div className="flex justify-end gap-3 mt-4"><button onClick={() => setEditOpen(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={() => submitForm(true)} disabled={submitting} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">{submitting ? 'Saving...' : 'Update'}</button></div>
        </Modal>

        <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)} title="Delete Recommendation">
          <p className="text-gray-600 mb-4">Delete recommendation for "{selected?.symbol_name}"? This cannot be undone.</p>
          <div className="flex justify-end gap-3"><button onClick={() => setDeleteOpen(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={deleteRec} disabled={submitting} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">{submitting ? 'Deleting...' : 'Delete'}</button></div>
        </Modal>

        <Modal open={bulkOpen} onClose={() => setBulkOpen(false)} title="Bulk Upload (JSON)">
          <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded text-sm mb-3">Paste a JSON array of recommendation objects.</div>
          <textarea value={bulkText} onChange={e => setBulkText(e.target.value)} rows={10} placeholder='[{"symbol_name":"EXAMPLE","title":"...","recommendation":"..."}]'
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono outline-none focus:ring-2 focus:ring-blue-500 resize-y mb-3" />
          <label className="flex items-center gap-2 text-sm mb-4">
            <input type="checkbox" checked={bulkUpdateOnly} onChange={e => setBulkUpdateOnly(e.target.checked)} className="rounded" />Update only (skip missing)
          </label>
          <div className="flex justify-end gap-3">
            <button onClick={() => setBulkOpen(false)} disabled={bulkSubmitting} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
            <button disabled={bulkSubmitting} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              onClick={async () => {
                try { setBulkSubmitting(true); const items = JSON.parse(bulkText); if (!Array.isArray(items) || !items.length) { toast.error('Must be non-empty array'); return; }
                  const r = await axios.post('/recommendations/admin/bulk', { items, updateOnly: bulkUpdateOnly });
                  toast.success(`Done. Updated: ${r.data.updated}, Inserted: ${r.data.inserted}`); setBulkOpen(false); setBulkText(''); fetchRecs();
                } catch (e) { toast.error(e instanceof SyntaxError ? 'Invalid JSON' : (e.response?.data?.message || 'Failed')); }
                finally { setBulkSubmitting(false); }
              }}>{bulkSubmitting ? 'Uploading...' : 'Upload'}</button>
          </div>
        </Modal>
      </div>
    </>
  );
}

export default SymbolRecommendations;
